/**
 * ConnectedApp: wires Dexie + Supabase session + sync to App props.
 * Paints immediately from local DB (docs/08 §10); network syncs in background.
 * Auth panel gates network only — local UI paints without auth.
 *
 * This file lives outside src/ui to allow direct DB access (ESLint rule 70).
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import type { SupabaseClient } from '@supabase/supabase-js';
import { openCardZappDb, type CardZappDB } from '../db';
import { businessDate, type BusinessDate } from '../domain/time';
import { buildAppPropsFromDb, type BuildAppPropsResult } from './buildAppPropsFromDb';
import { App, type AppProps } from '../ui/App';
import type { QuickPurchaseConfirmArgs } from '../ui/quick-purchase';
import type { SpendCategory } from '../domain/recommend/types';
import type { SyncStatusProps } from '../ui/sync';
import { getSyncPushStatus, flushOutbox, pullTables } from '../sync';
import { confirmPurchase } from '../repositories/confirmPurchase';
import { correctMerchantCategory } from '../repositories/correctMerchantCategory';
import { flagRecurringCharge } from '../repositories/flagRecurringCharge';
import { createCardZappSupabaseClient } from '../repositories/supabaseClient';
import { createSupabaseTransport } from '../repositories/supabaseTransport';
import { createRemoteRepository } from '../repositories/remoteRepository';

const SYNC_ENABLED_KEY = 'VITE_SYNC_ENABLED';

function readSyncEnabled(): boolean {
  try {
    const env = import.meta.env as Record<string, string | undefined>;
    const val = env[SYNC_ENABLED_KEY];
    return val === 'true' || val === '1';
  } catch {
    return false;
  }
}

function readSupabaseEnvSafe(): { url: string; anonKey: string } | null {
  try {
    const env = import.meta.env as Record<string, string | undefined>;
    const url = env['VITE_SUPABASE_URL'];
    const anonKey = env['VITE_SUPABASE_ANON_KEY'];
    if (!url || !anonKey || anonKey.startsWith('replace-with-')) return null;
    return { url: url.replace(/\/rest\/v1\/?$/, '').replace(/\/$/, ''), anonKey };
  } catch {
    return null;
  }
}

type AuthState = 'checking' | 'signed_out' | 'signing_in' | 'signed_in' | 'disabled';

function AuthPanel({
  state,
  onSignIn,
  error,
}: {
  state: AuthState;
  onSignIn: (email: string) => Promise<boolean>;
  error: string | null;
}): JSX.Element | null {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  if (state === 'disabled' || state === 'signed_in') return null;
  if (state === 'checking') {
    return (
      <section className="mb-4 rounded-lg border border-slate-800 bg-slate-900/60 p-4">
        <p className="text-sm text-slate-400">Checking session…</p>
      </section>
    );
  }

  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault();
    if (!email.trim() || submitting) return;
    setSubmitting(true);
    setSent(false);
    void (async () => {
      const ok = await onSignIn(email.trim());
      setSubmitting(false);
      if (ok) setSent(true);
    })();
  };

  return (
    <section className="mb-4 rounded-lg border border-slate-800 bg-slate-900/60 p-4">
      <h2 className="text-sm font-medium text-slate-300">Sign in to sync</h2>
      {sent ? (
        <p className="mt-2 text-sm text-emerald-400">Check your email for a magic link.</p>
      ) : (
        <form onSubmit={handleSubmit} className="mt-2 flex gap-2">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email"
            className="flex-1 rounded bg-slate-800 px-3 py-1.5 text-sm text-slate-200 placeholder-slate-500 outline-none focus:ring-1 focus:ring-sky-500"
            disabled={submitting}
            autoComplete="email"
          />
          <button
            type="submit"
            disabled={submitting || !email.trim()}
            className="rounded bg-sky-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-sky-500 disabled:opacity-50"
          >
            {submitting ? '…' : 'Send link'}
          </button>
        </form>
      )}
      {error !== null ? <p className="mt-2 text-sm text-rose-400">{error}</p> : null}
    </section>
  );
}

export function ConnectedApp(): JSX.Element {
  const dbRef = useRef<CardZappDB | null>(null);
  const [db, setDb] = useState<CardZappDB | null>(null);
  const asOf = useMemo<BusinessDate>(() => {
    const d = new Date();
    return businessDate(d.toISOString().slice(0, 10));
  }, []);
  const syncEnabled = useMemo(() => readSyncEnabled(), []);
  const supabaseEnv = useMemo(() => readSupabaseEnvSafe(), []);

  const [authState, setAuthState] = useState<AuthState>(syncEnabled ? 'checking' : 'disabled');
  const [authError, setAuthError] = useState<string | null>(null);
  const supabaseRef = useRef<SupabaseClient | null>(null);

  useEffect(() => {
    const instance = openCardZappDb();
    dbRef.current = instance;
    setDb(instance);
    return () => {
      instance.close();
    };
  }, []);

  useEffect(() => {
    if (!syncEnabled || supabaseEnv === null) {
      setAuthState('disabled');
      return;
    }

    let cancelled = false;
    const client = createCardZappSupabaseClient(supabaseEnv);
    supabaseRef.current = client;

    void (async () => {
      const { data } = await client.auth.getSession();
      if (cancelled) return;

      if (data.session !== null) {
        setAuthState('signed_in');
      } else {
        setAuthState('signed_out');
      }
    })();

    const { data: sub } = client.auth.onAuthStateChange((_event, session) => {
      setAuthState(session !== null ? 'signed_in' : 'signed_out');
    });

    return () => {
      cancelled = true;
      sub.subscription.unsubscribe();
    };
  }, [syncEnabled, supabaseEnv]);

  useEffect(() => {
    if (authState !== 'signed_in' || db === null || supabaseRef.current === null) return;

    let cancelled = false;
    void (async () => {
      try {
        const supabase = supabaseRef.current;
        if (supabase === null) return;
        const transport = createSupabaseTransport(supabase);
        const remote = createRemoteRepository(transport);

        await flushOutbox({ db, remote, nowIso: new Date().toISOString() });
        if (cancelled) return;
        await pullTables({ db, remote, nowIso: new Date().toISOString() });
      } catch {
        // Sync errors are non-fatal; local continues working.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [authState, db]);

  const handleSignIn = useCallback(async (email: string): Promise<boolean> => {
    const client = supabaseRef.current;
    if (client === null) return false;
    setAuthError(null);
    try {
      const { error } = await client.auth.signInWithOtp({
        email,
        options: { emailRedirectTo: window.location.origin },
      });
      if (error !== null) {
        setAuthError(error.message);
        return false;
      }
      return true;
    } catch (err) {
      setAuthError(err instanceof Error ? err.message : 'Sign-in failed');
      return false;
    }
  }, []);

  const propsResult = useLiveQuery(
    async (): Promise<BuildAppPropsResult | null> => {
      if (db === null) return null;
      return buildAppPropsFromDb(db, asOf);
    },
    [db, asOf],
    null,
  );

  const syncStatus = useLiveQuery(
    async (): Promise<SyncStatusProps | null> => {
      if (db === null) return null;
      return getSyncPushStatus(db);
    },
    [db],
    null,
  );

  const handleConfirmPurchase = useCallback(
    async (args: QuickPurchaseConfirmArgs): Promise<void> => {
      if (db === null) return;
      const now = new Date().toISOString();
      await confirmPurchase({
        db,
        merchant_input: args.merchant_input,
        merchant_id: args.rank_input.query.merchant_id,
        description: args.description,
        amount_cents: args.amount_cents,
        exact_total_cents: args.exact_total_cents,
        transaction_date: args.transaction_date,
        card_id_used: args.card_id_used,
        checking_account_label: args.checking_account_label,
        rank_input: args.rank_input,
        recommendation: args.recommendation,
        rules_version: 'v1',
        now_iso: now,
      });
    },
    [db],
  );

  const handleCorrectCategory = useCallback(
    async (args: { transaction_id: string; corrected_category: SpendCategory }): Promise<void> => {
      if (db === null) return;
      const now = new Date().toISOString();
      await correctMerchantCategory({
        db,
        transaction_id: args.transaction_id,
        corrected_category: args.corrected_category,
        observed_at: asOf,
        now_iso: now,
      });
    },
    [db, asOf],
  );

  const handleFlagRecurring = useCallback(
    async (args: { transaction_id: string }): Promise<void> => {
      if (db === null) return;
      const now = new Date().toISOString();
      await flagRecurringCharge({
        db,
        transaction_id: args.transaction_id,
        now_iso: now,
      });
    },
    [db],
  );

  if (db === null || propsResult === null) {
    return (
      <main className="mx-auto flex min-h-dvh max-w-md flex-col gap-6 px-5 py-10">
        <p className="text-sm text-slate-400">Loading…</p>
      </main>
    );
  }

  const appProps: AppProps = {
    ...propsResult,
    onConfirmPurchase: handleConfirmPurchase,
    onCorrectCategory: handleCorrectCategory,
    onFlagRecurring: handleFlagRecurring,
    syncStatus: syncStatus ?? null,
  };

  return (
    <>
      {syncEnabled ? (
        <div className="mx-auto max-w-md px-5 pt-10">
          <AuthPanel state={authState} onSignIn={handleSignIn} error={authError} />
        </div>
      ) : null}
      <App {...appProps} />
    </>
  );
}
